import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  DollarSign, 
  Users, 
  TrendingUp, 
  Instagram, 
  MessageCircle, 
  CreditCard,
  Share2,
  BarChart3,
  Settings,
  Play,
  CheckCircle2
} from 'lucide-react';

interface Campaign {
  id: string;
  name: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface FunnelStage {
  stage: string;
  users: number;
  conversionRate: number;
  dropoffRate: number;
}

interface Metrics {
  total_payments: number;
  total_revenue: number;
  average_order_value: number;
  paying_users: number;
  subscriptions: number;
  one_time_purchases: number;
}

const FlywheelDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [funnel, setFunnel] = useState<FunnelStage[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [testResult, setTestResult] = useState<any>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'https://biig-api.b-9f2.workers.dev';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignsRes, funnelRes, metricsRes] = await Promise.all([
        fetch(`${API_BASE}/api/flywheel/campaigns`),
        fetch(`${API_BASE}/api/flywheel/analytics/funnel`),
        fetch(`${API_BASE}/api/flywheel/analytics/revenue`)
      ]);

      const campaignsData = await campaignsRes.json();
      const funnelData = await funnelRes.json();
      const metricsData = await metricsRes.json();

      setCampaigns(campaignsData.campaigns || []);
      setFunnel(funnelData.funnel || []);
      setMetrics(metricsData.metrics || null);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runFullLoopTest = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/flywheel/test/full-loop`, {
        method: 'POST'
      });
      const result = await response.json();
      setTestResult(result);
    } catch (error) {
      console.error('Error running test:', error);
    }
  };

  const stageIcons: Record<string, React.ReactNode> = {
    'ig_dm': <Instagram className="w-5 h-5" />,
    'wa_contacted': <MessageCircle className="w-5 h-5" />,
    'assets_delivered': <Share2 className="w-5 h-5" />,
    'payment_completed': <CreditCard className="w-5 h-5" />,
    'ugc_completed': <CheckCircle2 className="w-5 h-5" />
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: <Activity className="w-4 h-4" /> },
    { id: 'campaigns', label: 'Campaigns', icon: <Settings className="w-4 h-4" /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'test', label: 'Test Loop', icon: <Play className="w-4 h-4" /> }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-2">
            Growth Flywheel Dashboard
          </h1>
          <p className="text-gray-300">
            Instagram → WhatsApp → Revenue → UGC → Viral Loop
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              {tab.icon}
              {tab.label}
            </motion.button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-gray-800 rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <DollarSign className="w-8 h-8 text-green-500" />
                    <span className="text-xs text-gray-400">Revenue</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    ${((metrics?.total_revenue || 0) / 100).toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-400 mt-2">
                    {metrics?.total_payments || 0} payments
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gray-800 rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-8 h-8 text-blue-500" />
                    <span className="text-xs text-gray-400">Users</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {metrics?.paying_users || 0}
                  </div>
                  <div className="text-sm text-gray-400 mt-2">
                    Paying customers
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gray-800 rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <TrendingUp className="w-8 h-8 text-purple-500" />
                    <span className="text-xs text-gray-400">AOV</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    ${((metrics?.average_order_value || 0) / 100).toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-400 mt-2">
                    Average order
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gray-800 rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Activity className="w-8 h-8 text-orange-500" />
                    <span className="text-xs text-gray-400">Active</span>
                  </div>
                  <div className="text-2xl font-bold text-white">
                    {campaigns.filter(c => c.active).length}
                  </div>
                  <div className="text-sm text-gray-400 mt-2">
                    Campaigns running
                  </div>
                </motion.div>
              </div>
            )}

            {/* Campaigns Tab */}
            {activeTab === 'campaigns' && (
              <div className="space-y-4">
                {campaigns.map((campaign, index) => (
                  <motion.div
                    key={campaign.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-800 rounded-xl p-6"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-white">
                          {campaign.name}
                        </h3>
                        <p className="text-sm text-gray-400 mt-1">
                          ID: {campaign.id}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          campaign.active 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-gray-600/20 text-gray-400'
                        }`}>
                          {campaign.active ? 'Active' : 'Inactive'}
                        </span>
                        <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                          Configure
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div className="bg-gray-800 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Conversion Funnel
                  </h3>
                  <div className="space-y-4">
                    {funnel.map((stage, index) => (
                      <motion.div
                        key={stage.stage}
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: '100%' }}
                        transition={{ delay: index * 0.1 }}
                        className="relative"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {stageIcons[stage.stage] || <Activity className="w-5 h-5" />}
                            <span className="text-white font-medium">
                              {stage.stage.replace(/_/g, ' ').toUpperCase()}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-white font-bold">
                              {stage.users}
                            </span>
                            <span className="text-gray-400 ml-2">
                              ({(stage.conversionRate * 100).toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${stage.conversionRate * 100}%` }}
                            transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                            className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-800 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Revenue Breakdown
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Subscriptions</span>
                        <span className="text-white font-bold">
                          {metrics?.subscriptions || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">One-time</span>
                        <span className="text-white font-bold">
                          {metrics?.one_time_purchases || 0}
                        </span>
                      </div>
                      <div className="pt-3 border-t border-gray-700">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400">Total Revenue</span>
                          <span className="text-green-400 font-bold text-xl">
                            ${((metrics?.total_revenue || 0) / 100).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-xl p-6">
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Viral Metrics
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Viral Coefficient</span>
                        <span className="text-white font-bold">1.5x</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">UGC Posts</span>
                        <span className="text-white font-bold">42</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400">Reach Multiplier</span>
                        <span className="text-white font-bold">3.2x</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Test Tab */}
            {activeTab === 'test' && (
              <div className="space-y-6">
                <div className="bg-gray-800 rounded-xl p-6">
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Test the Complete Loop
                  </h3>
                  <p className="text-gray-400 mb-6">
                    Run a full simulation of the growth flywheel to verify all components are working.
                  </p>
                  <button
                    onClick={runFullLoopTest}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all"
                  >
                    <Play className="inline-block w-5 h-5 mr-2" />
                    Run Full Loop Test
                  </button>
                </div>

                {testResult && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-800 rounded-xl p-6"
                  >
                    <h3 className="text-xl font-semibold text-white mb-4">
                      Test Results
                    </h3>
                    {testResult.success ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle2 className="w-6 h-6" />
                          <span className="font-medium">Loop Complete!</span>
                        </div>
                        <div className="space-y-2">
                          {testResult.steps?.map((step: any, index: number) => (
                            <div key={index} className="flex items-start gap-3 text-sm">
                              <span className="text-purple-400 font-bold">
                                {step.step}.
                              </span>
                              <div className="flex-1">
                                <span className="text-white">{step.action}</span>
                                {step.keyword && (
                                  <span className="text-gray-400 ml-2">
                                    Keyword: {step.keyword}
                                  </span>
                                )}
                                {step.whatsappLink && (
                                  <span className="text-blue-400 ml-2">
                                    Link generated
                                  </span>
                                )}
                                {step.amount && (
                                  <span className="text-green-400 ml-2">
                                    ${step.amount / 100}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {testResult.metrics && (
                          <div className="mt-4 pt-4 border-t border-gray-700">
                            <h4 className="text-white font-medium mb-2">Metrics</h4>
                            <div className="grid grid-cols-3 gap-4">
                              <div>
                                <span className="text-gray-400 text-sm">Conversion</span>
                                <div className="text-white font-bold">
                                  {testResult.metrics.conversionRate}
                                </div>
                              </div>
                              <div>
                                <span className="text-gray-400 text-sm">AOV</span>
                                <div className="text-white font-bold">
                                  {testResult.metrics.avgOrderValue}
                                </div>
                              </div>
                              <div>
                                <span className="text-gray-400 text-sm">Viral Coef</span>
                                <div className="text-white font-bold">
                                  {testResult.metrics.viralCoefficient}x
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-red-400">Test failed. Check console for details.</div>
                    )}
                  </motion.div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FlywheelDashboard;